// eslint-disable-next-line @typescript-eslint/no-unused-vars
import type { RenderResultWithSsr } from '../../lib/renderTypes';
import { button, div, h2, i, li, p, span, ul } from '../../render/html';
import type { MapDataFeature } from '../data/mapDataType';
import {
  ELEMENT_CLASS_NAME,
  MAP_ACCORDION_CONTENT_ID,
  MAP_OUTER_ELEMENT_ID,
} from '../mapConstants';
import { INITIAL_MAP_ONLINE_SERVICE } from '../../state/createInitialState';
import type {
  MapOnlineService,
  StateMapData,
} from '../../state/createInitialState';
import { REGSCHL_BUND, REGSCHL_HESSEN } from '../../constants';
import {
  ODMAP__ORT_INPUT__TITEL,
  ODMAP__HINWEIS,
  ODMAP__TITEL,
  ODMAP__X_ONLINEDIENSTE_VERFUEGBAR,
  ODMAP__HINWEIS__ONLINEDIENSTE_WERDEN_BERECHNET,
  ODMAP__OD_WERDEN_BEREITGESTELLT_VON,
  COMMON__BUND,
  COMMON__HESSEN,
  COMMON__LAND_HESSEN,
  ODMAP__ORT_INPUT__BESCHREIBUNG,
} from '../../texts';
import { cityField } from '../../view/blocks/citySearch';
import type { Dropdown } from '../../action/search/searchCommon';
import type { Gebiet } from '../../action/search/searchTypes';
import {
  MAP_CITY_CHANGED,
  MAP_CITY_INPUT_CHANGED,
  MAP_DROPDOWN_HIDDEN,
  MAP_DROPDOWN_SELECTED,
  MAP_DROPDOWN_SHOWN,
} from '../mapEvents';
import type { MapOnlineCountsResult } from '../mapEvents';
import { SKIP_CONTENT } from '../../render/incremental-hyperscript';
import type { SimpleStore } from '../../state/SimpleStore';
import loadingOverlay from '../../view/blocks/loadingOverlay';
import type { Flags } from '../../history/flags';
import { getSuggestion } from '../../action/search/handleCityChanged';
import combinePlaceNames, {
  combinePlaceNamesForInput,
  simplePlaceName,
} from '../../view/util/combinePlaceNames';

type CountElement = {
  label: string;
  count: number;
};

type GebietForMap = Gebiet & {
  kommunenLabel: string;
};

function prepareData(
  flags: Flags,
  mapData: StateMapData,
  onlineServicesByRegschl: MapOnlineCountsResult,
  mapState: MapOnlineService,
  cityRegschl: string,
) {
  let selectedRegschl;
  if (flags.map_od === 'synced') {
    selectedRegschl = cityRegschl;
  } else {
    ({ selectedRegschl } = mapState);
  }
  const regschlKreisfrei: string[] = [];
  mapData.kommunen.forEach((entry) => {
    const regschl = entry.properties.RS;
    // kreisfreie Städte erkennen
    if (regschl.substring(5) === '0000000') {
      regschlKreisfrei.push(regschl);
    }
  });
  const kreisfreiStaedteList: [string, MapDataFeature][] = [];
  const kreiseList = mapData.landkreise
    .map((entry): [string, Gebiet & { kreis: string }] => {
      const kreisschl = entry.properties.RS;
      const label = entry.properties.GEN;
      const regschl = `${kreisschl}0000000`;
      if (regschlKreisfrei.includes(regschl)) {
        kreisfreiStaedteList.push([regschl, entry]);
      }
      return [
        regschl,
        {
          id: regschl,
          land: COMMON__HESSEN,
          ort: undefined,
          bezirk: undefined,
          kreis: label,
          verband: undefined,
        },
      ];
    })
    .filter(([regschl]) => !regschlKreisfrei.includes(regschl));
  const kreiseByRegschl = new Map(kreiseList);

  const kreisfreiStaedteByRegschl = new Map(kreisfreiStaedteList);

  const kommunenList = mapData.kommunen.map((entry): [string, GebietForMap] => {
    const regschl = entry.properties.RS;
    const kommuneOrKreisfrei = kreisfreiStaedteByRegschl.get(regschl) ?? entry;
    const label = kommuneOrKreisfrei.properties.GEN;
    const kommunenLabel = kommuneOrKreisfrei.properties.BEZ;
    return [
      regschl,
      {
        id: regschl,
        land: COMMON__HESSEN,
        ort: label,
        kommunenLabel,
        bezirk: undefined,
        kreis: kreiseByRegschl.get(regschl)?.kreis,
        verband: undefined,
      },
    ];
  });
  const kommunenByRegschl = new Map(kommunenList);

  const kommune = kommunenByRegschl.get(selectedRegschl);
  const kreis = kreiseByRegschl.get(
    `${selectedRegschl.substring(0, 5)}0000000`,
  );
  const kommuneEntry = kommune
    ? [
        {
          first: false,
          last: false,
          regschl: selectedRegschl,
          title: simplePlaceName(kommune),
        },
      ]
    : [];
  const kreisEntry = kreis
    ? [
        {
          first: false,
          last: false,
          regschl: `${selectedRegschl.substring(0, 5)}0000000`,
          title: simplePlaceName(kreis),
        },
      ]
    : [];
  const hessenEntry = {
    first: false,
    last: false,
    regschl: REGSCHL_HESSEN,
    title: COMMON__LAND_HESSEN,
  };
  const breadcrumbEntries = [hessenEntry, ...kreisEntry, ...kommuneEntry];
  breadcrumbEntries[0].first = true;
  breadcrumbEntries[breadcrumbEntries.length - 1].last = true;

  // Eingabewert
  const { searchTerm } = mapState.input;

  let inputValue: string;
  if (searchTerm === undefined) {
    if (selectedRegschl === REGSCHL_HESSEN) {
      inputValue = '';
    } else if (kommune) {
      inputValue = combinePlaceNamesForInput(kommune);
    } else if (kreis) {
      inputValue = combinePlaceNamesForInput(kreis);
    } else {
      inputValue = '';
    }
  } else {
    inputValue = searchTerm;
  }

  // suggestions
  let suggestions: Gebiet[] = [];

  const inputValueSearch = inputValue.trim().toLowerCase();
  if (mapState.dropdown.visible && inputValueSearch) {
    suggestions = [
      ...kommunenList
        .filter(([, gebiet]) =>
          combinePlaceNames(gebiet).toLowerCase().includes(inputValueSearch),
        )
        .map(([, gebiet]) => gebiet),
      ...kreiseList
        .filter(([, gebiet]) =>
          combinePlaceNames(gebiet).toLowerCase().includes(inputValueSearch),
        )
        .map(([, gebiet]) => gebiet),
    ];
    if (COMMON__HESSEN.toLowerCase().includes(inputValueSearch)) {
      suggestions.unshift({
        id: REGSCHL_HESSEN,
        land: COMMON__HESSEN,
        ort: undefined,
        bezirk: undefined,
        kreis: undefined,
        verband: undefined,
      });
    }
  }

  let osCountEntries: CountElement[];
  let osCount: number;
  let countsAvailable: boolean;
  if (onlineServicesByRegschl) {
    const regschlBezirk =
      selectedRegschl.substring(2) !== '0000000000'
        ? `${selectedRegschl.substring(0, 3)}000000000`
        : undefined;
    const regschlLand =
      selectedRegschl !== REGSCHL_BUND
        ? `${selectedRegschl.substring(0, 2)}0000000000`
        : undefined;

    let kommuneCount: CountElement[] = [];
    if (kommune) {
      const count = onlineServicesByRegschl.counts[kommune.id];
      if (count) {
        kommuneCount = [
          {
            count,
            label: `${kommune.kommunenLabel} ${simplePlaceName(kommune)}`,
          },
        ];
      }
    }
    let kreisCount: CountElement[] = [];
    if (kreis) {
      const count = onlineServicesByRegschl.counts[kreis.id];
      if (count) {
        kreisCount = [
          {
            count,
            label: simplePlaceName(kreis),
          },
        ];
      }
    }
    let bezirkCount: CountElement[] = [];
    if (regschlBezirk) {
      const count = onlineServicesByRegschl.counts[regschlBezirk];
      if (count) {
        bezirkCount = [
          {
            count,
            label:
              onlineServicesByRegschl.bezirke.find(
                (value) => value.regschl === regschlBezirk,
              )?.label || regschlBezirk,
          },
        ];
      }
    }
    let landCount: CountElement[] = [];
    if (regschlLand) {
      const count = onlineServicesByRegschl.counts[regschlLand];
      if (count) {
        landCount = [
          {
            count,
            label: COMMON__LAND_HESSEN,
          },
        ];
      }
    }
    let bundCount: CountElement[] = [];
    const bundCountNumber = onlineServicesByRegschl.counts[REGSCHL_BUND];
    if (bundCountNumber) {
      bundCount = [
        {
          count: bundCountNumber,
          label: COMMON__BUND,
        },
      ];
    }

    osCountEntries = [
      ...kommuneCount,
      ...kreisCount,
      ...bezirkCount,
      ...landCount,
      ...bundCount,
    ];

    osCount = osCountEntries.reduce(
      (accumulator, currentValue) => accumulator + currentValue.count,
      0,
    );
    countsAvailable = true;
  } else {
    osCountEntries = [];
    osCount = 0;
    countsAvailable = false;
  }

  let label;
  if (kommune) {
    label = `${kommune.kommunenLabel} ${simplePlaceName(kommune)}`;
  } else if (kreis) {
    label = simplePlaceName(kreis);
  } else {
    label = COMMON__LAND_HESSEN;
  }

  return {
    breadcrumb: breadcrumbEntries,
    dropdown: mapState.dropdown,
    inputValue,
    suggestions,
    counts: {
      label,
      count: osCount,
      entries: osCountEntries,
      countsAvailable,
    },
  };
}

export const DATA_MAP_ID = 'data-map-id';
export const DATA_MAP_INSTANCE_ID = 'data-map-instance-id';
export function patchMapFrameAndOuterElements(
  simpleStoreForHandlers: SimpleStore,
  flags: Flags,
  mapDataOption: StateMapData | undefined,
  onlineServicesByRegschl: MapOnlineCountsResult,
  mapState: MapOnlineService,
  cityRegschl: string,
  mapDomId: string,
  loading: boolean,
): RenderResultWithSsr {
  const mapData = mapDataOption ?? {
    kommunen: [],
    landkreise: [],
  };
  const data = prepareData(
    flags,
    mapData,
    onlineServicesByRegschl,
    mapState,
    cityRegschl,
  );
  return div({ class: ELEMENT_CLASS_NAME, [DATA_MAP_ID]: mapDomId }, [
    h2({}, ODMAP__TITEL),
    div({ class: 'card p-3' }, [
      div({ class: 'container-fluid' }, [
        div({ class: 'row' }, [
          div({
            class: 'vwp-map-container col-xxl-6',
            id: MAP_OUTER_ELEMENT_ID(mapDomId),
            [SKIP_CONTENT]: true,
          }),
          div({ class: 'col-xxl-6' }, [
            breadcrumb(simpleStoreForHandlers, mapDomId, data.breadcrumb),
            p(
              { class: 'font-font-roc-grotesk-w05-regular mb-5' },
              ODMAP__HINWEIS,
            ),
            citySelection(
              simpleStoreForHandlers,
              mapDomId,
              data.inputValue,
              data.dropdown,
              data.suggestions,
            ),
            ...counts(mapDomId, data.counts),
          ]),
        ]),
        loading ? loadingOverlay() : undefined,
      ]),
    ]),
  ]);

  /*
        const detailsElement = document.createElement('div');
      detailsElement.style.border = '2px solid blue';
      detailsElement.style.width = '100%';
      detailsElement.style.padding = '1rem';
      detailsElement.innerHTML = `
      <p id="vwp-map-name"></p>
      <p id="vwp-map-regschl"></p>
      <p id="vwp-map-onlinedienste"></p>
      `;
      outerElement.innerHTML = '';
      outerElement.append(mapElement, detailsElement);

   */
}

type BreadcrumbEntry = {
  first: boolean;
  last: boolean;
  regschl: string;
  title: string;
};

function handleBreadcrumbEntryClicked(
  simpleStoreForHandlers: SimpleStore,
  mapDomId: string,
  regschl: string,
) {
  return () => {
    simpleStoreForHandlers.dispatch(MAP_CITY_CHANGED({ mapDomId, regschl }));
  };
}

function breadcrumb(
  simpleStoreForHandlers: SimpleStore,
  mapDomId: string,
  entries: BreadcrumbEntry[],
) {
  if (entries.length) {
    return div(
      { class: 'hzd-breadcrumb mb-3 font-font-roc-grotesk-w05-regular' },
      [
        ul(
          { class: 'hzd-list-really-unstyled' },
          entries.map((entry) =>
            li({ class: 'd-inline' }, [
              i({
                class: entry.first
                  ? 'hzd-breadcrumb-prefix bi-chevron-right bi'
                  : 'bi-chevron-right bi hzd-breadcrumb-separator',
              }),
              entry.last
                ? span(
                    {
                      class: `ps-1 ${
                        entry.first ? 'hzd-breadcrumb__first-item' : ''
                      }`,
                    },
                    entry.title,
                  )
                : button(
                    {
                      class: `btn btn-link hzd-breadcrumb-link font-font-roc-grotesk-w05-regular font-size-5 px-1 m-0 ${
                        entry.first ? 'hzd-breadcrumb__first-item' : ''
                      }`,
                      type: 'button',
                      onclick: handleBreadcrumbEntryClicked(
                        simpleStoreForHandlers,
                        mapDomId,
                        entry.regschl,
                      ),
                    },
                    entry.title,
                  ),
            ]),
          ),
        ),
      ],
    );
  } else {
    return div({ class: 'hzd-breadcrumb' });
  }
}

function handleCityBlurred(
  simpleStoreForHandlers: SimpleStore,
  mapDomId: string,
  suggestions: Gebiet[],
) {
  const state = simpleStoreForHandlers.getState();
  const mapState =
    state.mapOnlineServices.mapStates[mapDomId] ?? INITIAL_MAP_ONLINE_SERVICE;
  const city = mapState.input.searchTerm;
  // wenn city===undefined, entspricht der Input bereits dem gewählden Regschl → nichts zu tun
  if (city !== undefined) {
    handleCitySet(simpleStoreForHandlers, mapDomId, city, suggestions);
  }
}
function handleCitySet(
  simpleStoreForHandlers: SimpleStore,
  mapDomId: string,
  city: string,
  suggestions: Gebiet[],
) {
  const foundSuggestion = getSuggestion(suggestions, city);
  if (foundSuggestion) {
    simpleStoreForHandlers.dispatch(
      MAP_CITY_CHANGED({ mapDomId, regschl: foundSuggestion.id }),
    );
  } else if (COMMON__HESSEN.toLowerCase() === city.trim().toLowerCase()) {
    simpleStoreForHandlers.dispatch(
      MAP_CITY_CHANGED({ mapDomId, regschl: REGSCHL_HESSEN }),
    );
  } else {
    simpleStoreForHandlers.dispatch(
      MAP_CITY_INPUT_CHANGED({ mapDomId, input: undefined }),
    );
  }
}

function citySelection(
  simpleStoreForHandlers: SimpleStore,
  mapDomId: string,

  inputValue: string,
  dropdownState: Dropdown,
  suggestions: Gebiet[],
): RenderResultWithSsr {
  return cityField({
    handlers: {
      clearCity: () => {
        simpleStoreForHandlers.dispatch(
          MAP_CITY_CHANGED({ mapDomId, regschl: REGSCHL_HESSEN }),
        );
      },
      handleCityChanged: (city: string) => {
        simpleStoreForHandlers.dispatch(
          MAP_CITY_INPUT_CHANGED({ mapDomId, input: city }),
        );
      },
      selectDropdown: (index: number | undefined) => {
        simpleStoreForHandlers.dispatch(
          MAP_DROPDOWN_SELECTED({ mapDomId, index }),
        );
      },
      hideDropdown: () => {
        simpleStoreForHandlers.dispatch(MAP_DROPDOWN_HIDDEN(mapDomId));
      },
      showFeedback: () => {
        // do nothing for now
      },
      handleCityBlurred: () => {
        handleCityBlurred(simpleStoreForHandlers, mapDomId, suggestions);
      },
      handleCitySet: (city: string) => {
        handleCitySet(simpleStoreForHandlers, mapDomId, city, suggestions);
      },
      showDropdown: () => {
        simpleStoreForHandlers.dispatch(MAP_DROPDOWN_SHOWN(mapDomId));
      },
    },
    suggestions,
    dropdownState,
    labelText: ODMAP__ORT_INPUT__TITEL,
    loading: false,
    ariaDescription: ODMAP__ORT_INPUT__BESCHREIBUNG,
    placeholder: ODMAP__ORT_INPUT__BESCHREIBUNG,
    inputValue,
    showFeedbackHint: false,
  });
}

function counts(
  mapDomId: string,
  data: {
    label: string;
    count: number;
    entries: CountElement[];
    countsAvailable: boolean;
  },
): RenderResultWithSsr[] {
  if (data.countsAvailable) {
    return [
      p({ class: 'font-font-roc-grotesk-w05-medium mb-0' }, data.label),
      p(
        { class: 'font-font-roc-grotesk-w05-medium text-info' },
        ODMAP__X_ONLINEDIENSTE_VERFUEGBAR(data.count, true),
      ),
      div({ class: 'accordion' }, [
        div({ class: 'accordion-item' }, [
          div(
            { class: 'font-font-roc-grotesk-w05-regular accordion-header' },
            button(
              {
                class: 'py-2 accordion-button accordion-map-button collapsed',
                type: 'button',
                'data-bs-toggle': 'collapse',
                'data-bs-target': `#${MAP_ACCORDION_CONTENT_ID(mapDomId)}`,
              },
              [ODMAP__OD_WERDEN_BEREITGESTELLT_VON],
            ),
          ),
          div(
            {
              id: MAP_ACCORDION_CONTENT_ID(mapDomId),
              class: 'accordion-collapse collapse',
            },
            [
              div({ class: 'accordion-body' }, [
                ...data.entries
                  .map((entry) => [
                    p(
                      { class: 'font-font-roc-grotesk-w05-medium mb-0' },
                      entry.label,
                    ),
                    p(
                      { class: 'font-font-roc-grotesk-w05-medium text-info' },
                      ODMAP__X_ONLINEDIENSTE_VERFUEGBAR(entry.count, false),
                    ),
                  ])
                  .flat(),
              ]),
            ],
          ),
        ]),
      ]),
    ];
  } else {
    return [
      p({ class: 'font-font-roc-grotesk-w05-medium mb-0' }, data.label),
      p(
        { class: 'font-font-roc-grotesk-w05-medium text-info' },
        ODMAP__HINWEIS__ONLINEDIENSTE_WERDEN_BERECHNET,
      ),
    ];
  }
}
